import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatRp, notifyError } from "@/lib/api";
import { refundOf } from "@/lib/orderCancellation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/* Phase 3 — transisi refund manual memakai endpoint P0 (PENDING→PROCESSING→REFUNDED). Dynicty TIDAK mentransfer dana. */

export const REFUND_METHODS = [
  { v: "transfer_bank", l: "Transfer Bank" }, { v: "qris", l: "QRIS" }, { v: "cash", l: "Tunai" }, { v: "other", l: "Lainnya" },
];

export function RefundProcessingDialog({ open, order, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!order) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/refund/processing`);
      toast.success(`Refund #${order.order_number} ditandai sedang diproses`);
      onDone?.();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="refund-processing-dialog">
        <DialogHeader>
          <DialogTitle>Mulai Proses Refund #{order.order_number}</DialogTitle>
          <DialogDescription>
            Menandai bahwa toko <b>sedang mengembalikan</b> {formatRp(refundOf(order).amount)} ke pelanggan melalui bank/QRIS secara mandiri (di luar Dynicty). Status pelanggan berubah menjadi “Refund sedang diproses”.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-xl" onClick={onClose} disabled={busy} data-testid="refund-processing-close">Tutup</Button>
          <Button className="rounded-xl brand-btn-primary" onClick={submit} disabled={busy} data-testid="refund-processing-confirm">
            {busy ? "Menyimpan..." : "Tandai Sedang Diproses"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RefundCompleteDialog({ open, order, onClose, onDone }) {
  const [form, setForm] = useState({ amount: "", method: "transfer_bank", reference: "", note: "" });
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!order) return null;
  const rv = refundOf(order);
  const amount = Number(form.amount || rv.amount || 0);
  const valid = amount > 0 && !!form.method && !!(form.reference.trim() || form.note.trim()) && confirmed;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => { setForm({ amount: "", method: "transfer_bank", reference: "", note: "" }); setConfirmed(false); };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/refund/complete`, {
        amount, method: form.method, reference: form.reference.trim() || null, note: form.note.trim() || null,
      });
      toast.success(`Refund #${order.order_number} ditandai selesai`);
      reset(); onDone?.();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="refund-complete-dialog">
        <DialogHeader>
          <DialogTitle>Catat Refund Selesai #{order.order_number}</DialogTitle>
          <DialogDescription>Isi bukti transfer/pengembalian yang sudah Anda lakukan. Status refund menjadi final dan tidak dapat diubah.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input type="number" min="1" className="rounded-xl" placeholder={`Nominal (default ${formatRp(rv.amount ?? 0)})`} value={form.amount} onChange={set("amount")} data-testid="refund-amount-input" />
          <select className="w-full h-10 rounded-xl border border-[#E5E5E5] px-3 text-sm bg-white" value={form.method} onChange={set("method")} data-testid="refund-method-select">
            {REFUND_METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <Input className="rounded-xl" placeholder="Referensi transaksi (no. transfer / ID QRIS)" value={form.reference} onChange={set("reference")} data-testid="refund-reference-input" />
          <Input className="rounded-xl" placeholder="Catatan (wajib jika referensi kosong)" value={form.note} onChange={set("note")} data-testid="refund-note-input" />
          <label className="flex gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="refund-complete-confirm-checkbox" />
            <span>Saya mengonfirmasi dana <b>{formatRp(amount)}</b> sudah dikembalikan ke pelanggan di luar sistem. <b>Dynicty tidak mentransfer dana</b>; pencatatan ini hanya menutup kewajiban refund.</span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => { reset(); onClose(); }} disabled={busy} data-testid="refund-complete-close">Tutup</Button>
          <Button className="rounded-xl brand-btn-primary" onClick={submit} disabled={!valid || busy} data-testid="refund-complete-confirm">
            {busy ? "Menyimpan..." : "Tandai Refund Selesai"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
